import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { apiClient } from "../../services/apiClientService";
import { useUserAuth } from "../../context/UserAuthContext";
import ReviewForm from "./ReviewForm";
import TrackOrderTimeline from "./TrackOrderTimeline";

const OrderDetailPage = () => {
  const { id } = useParams();
  const { user: authUser } = useUserAuth();
  const [order, setOrder] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const authUserId = authUser?._id || authUser?.id || "";

  const loadReviews = useCallback(async () => {
    const products = order?.products;
    if (!products?.length) {
      setReviews([]);
      return;
    }

    try {
      const entries = await Promise.all(
        products.map(async (item) => {
          const productId = item.productId;
          if (!productId) {
            return { productId: null, reviews: [] };
          }
          const res = await apiClient("GET", `/api/reviews/product/${productId}`, null, {
            skipLoading: true,
          });
          return {
            productId,
            reviews: Array.isArray(res?.reviews) ? res.reviews : [],
          };
        }),
      );
      setReviews(entries.filter((e) => e.productId));
    } catch (e) {
      console.error("Failed to load reviews:", e?.message || e);
      setReviews([]);
    }
  }, [order]);

  useEffect(() => {
    let cancelled = false;
    const loadOrder = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await apiClient("GET", `/api/orders/${id}`);
        if (cancelled) return;
        setOrder(res?.order || null);
        if (!res?.order) {
          setError("Order not found.");
        }
      } catch (e) {
        if (!cancelled) {
          setError(e?.message || "Could not load this order.");
          setOrder(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (id) loadOrder();
    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    if (order) loadReviews();
  }, [order, loadReviews]);

  const products = order?.products ?? [];

  return (
    <div className="mx-auto min-h-[50vh] max-w-3xl px-4 py-8 sm:px-5">
      <Link
        to="/user/dashboard"
        className="inline-flex items-center gap-1 text-sm font-semibold text-orange-700 transition hover:text-orange-900"
      >
        ← Back to orders
      </Link>

      <h1 className="mt-4 text-2xl font-extrabold tracking-tight text-stone-900 sm:text-3xl">Order details</h1>

      {loading ? (
        <p className="mt-8 text-sm text-stone-500">Loading order…</p>
      ) : error ? (
        <p className="mt-8 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</p>
      ) : (
        <div className="mt-8 space-y-6">
          <div className="rounded-2xl border border-stone-200 bg-stone-50/50 px-4 py-3 text-sm text-stone-700">
            <p>
              <span className="font-semibold text-stone-900">Order</span>{" "}
              <span className="font-mono text-xs text-stone-600">{order?._id}</span>
            </p>
            {order?.createdAt ? (
              <p className="mt-1 text-xs text-stone-500">
                Placed {new Date(order.createdAt).toLocaleString()}
              </p>
            ) : null}
            {order?.status ? (
              <p className="mt-2 flex flex-wrap gap-2">
                <span className="rounded-full bg-white px-2 py-0.5 text-xs font-bold uppercase tracking-wide text-orange-800 ring-1 ring-orange-200">
                  {order.status}
                </span>
                {order.sellerDecision === "pending" ? (
                  <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-bold uppercase tracking-wide text-amber-900">
                    Awaiting seller
                  </span>
                ) : null}
              </p>
            ) : null}
          </div>

          {order?.itemReadyAt ? (
            <div className="rounded-2xl border border-orange-200 bg-gradient-to-r from-orange-50 to-amber-50 px-4 py-3 text-sm text-stone-800 shadow-sm">
              <p className="font-bold text-orange-900">
                {String(order.shippingInfo?.receivingMethod || "").toLowerCase() === "pickup"
                  ? "Your order is ready for pickup"
                  : "Your order is ready for delivery"}
              </p>
              <p className="mt-1 text-stone-700">
                The seller marked your items as prepared on{" "}
                {new Date(order.itemReadyAt).toLocaleString()}.
                {String(order.shippingInfo?.receivingMethod || "").toLowerCase() === "pickup"
                  ? " Check your email for pickup location details."
                  : " You’ll get updates when it ships."}
              </p>
            </div>
          ) : null}

          <TrackOrderTimeline order={order} />

          {products.length === 0 ? (
            <p className="text-sm text-stone-600">No line items on this order.</p>
          ) : (
            products.map((item, idx) => {
              const productId = item.productId;
              const productReviews =
                reviews.find((r) => r.productId === productId)?.reviews || [];
              const hasReviewed = productReviews.some(
                (r) => String(r.user?._id || r.user || "") === String(authUserId),
              );

              return (
                <div
                  key={`${productId || idx}-${idx}`}
                  className="rounded-2xl border border-stone-200/90 bg-white p-4 shadow-sm sm:p-5"
                >
                  <p className="font-semibold text-stone-900">{item.name}</p>
                  <p className="mt-1 text-sm text-stone-600">
                    Qty {item.quantity} ·{" "}
                    <span className="font-medium tabular-nums text-stone-800">
                      ₹{Number(item.price).toFixed(2)}
                    </span>
                  </p>

                  <div className="mt-4 border-t border-stone-100 pt-4">
                    {hasReviewed ? (
                      <p className="text-sm font-medium text-emerald-700">You already reviewed this product.</p>
                    ) : (
                      <ReviewForm
                        productId={productId}
                        orderId={order._id}
                        onSubmitted={loadReviews}
                      />
                    )}
                  </div>

                  <div className="mt-4">
                    <h4 className="text-xs font-bold uppercase tracking-wide text-stone-500">Reviews</h4>
                    {productReviews.length === 0 ? (
                      <p className="mt-2 text-sm text-stone-500">No public reviews yet.</p>
                    ) : (
                      <ul className="mt-2 space-y-2">
                        {productReviews.map((r) => (
                          <li key={r._id} className="rounded-lg border border-stone-100 bg-stone-50/80 px-3 py-2 text-sm">
                            <p className="font-medium text-stone-900">
                              {r.user?.name || "Customer"} — ★ {r.rating}
                            </p>
                            {r.comment ? <p className="mt-1 text-stone-600">{r.comment}</p> : null}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default OrderDetailPage;
